/**
 * Lo que se sabe del aparato sin preguntarle a nadie: que es, como se llama
 * para una persona, y si la app corre instalada.
 *
 * Todo lo que decide entra como `DeviceEnv` y no se lee de `navigator` por
 * dentro, para poder probar cada aparato con su `userAgent` sin tenerlo en la
 * mano (`device.test.ts`). Lo unico que toca el navegador son `currentEnv` e
 * `isStandalone`.
 */

export interface DeviceEnv {
  /** `navigator.userAgent`. */
  ua: string;
  /** `navigator.platform`: el iPad moderno se anuncia como Mac. */
  platform: string;
  /** `navigator.maxTouchPoints`: y el multitactil lo delata. */
  touchPoints: number;
}

export type Platform = 'ios' | 'android' | 'macos' | 'windows' | 'linux' | 'unknown';

export function currentEnv(): DeviceEnv {
  if (typeof navigator === 'undefined') return { ua: '', platform: '', touchPoints: 0 };
  return {
    ua: navigator.userAgent,
    platform: navigator.platform ?? '',
    touchPoints: navigator.maxTouchPoints ?? 0,
  };
}

/**
 * Si es un iPad. Desde iPadOS 13 Safari en iPad se anuncia como Mac: solo el
 * multitactil lo delata. Sin esto, el iPad recibia las instrucciones de la Mac
 * —"Archivo, Agregar al Dock"— que en un iPad no existen.
 */
export const isIpad = ({ ua, platform, touchPoints }: DeviceEnv): boolean =>
  /iPad/.test(ua) || (platform === 'MacIntel' && touchPoints > 1);

export const isIos = (env: DeviceEnv): boolean => /iPhone|iPod/.test(env.ua) || isIpad(env);

export function platformOf(env: DeviceEnv): Platform {
  if (isIos(env)) return 'ios';
  if (/Android/.test(env.ua)) return 'android';
  if (/Windows/.test(env.ua)) return 'windows';
  if (/Macintosh|Mac OS X/.test(env.ua)) return 'macos';
  if (/Linux|CrOS/.test(env.ua)) return 'linux';
  return 'unknown';
}

/** "iPhone" o "iPad": lo que se le dice a la persona que tiene uno en la mano. */
export const iosDeviceName = (env: DeviceEnv): 'iPhone' | 'iPad' => (isIpad(env) ? 'iPad' : 'iPhone');

/**
 * Si la app corre instalada (desde su icono) y no en una pestaña.
 *
 * Son las MISMAS cuatro señales que el guion del `<head>` de `index.html` usa
 * para anclar el cuerpo, y por el mismo motivo: cada plataforma usa la suya.
 * `standalone` (Android, escritorio), `fullscreen`, `window-controls-overlay`
 * (Chrome de escritorio) y `navigator.standalone`, que es la unica fiable en un
 * iPhone, donde la app instalada se lanza por `apple-mobile-web-app-capable` y
 * la consulta de medios no siempre coincide (CLAUDE.md §13).
 *
 * Aqui se pregunta en vivo y no se lee el atributo `data-standalone`: ese se
 * marca una vez al cargar, y en Chrome de escritorio instalar la app no cambia
 * la pestaña en la que se estaba.
 */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const mode = (q: string): boolean => window.matchMedia?.(`(display-mode: ${q})`).matches ?? false;
  return (
    mode('standalone') ||
    mode('fullscreen') ||
    mode('window-controls-overlay') ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/**
 * "Chrome en Android", "Safari en iPhone": la etiqueta con la que la persona
 * reconoce su aparato en la lista de Configuracion. Es para leerse, no una
 * identificacion: dos telefonos iguales se llaman igual, y por eso la lista
 * tambien dice desde cuando y cual es este.
 */
export function deviceName(env: DeviceEnv): string {
  const { ua } = env;
  const system = /Android/.test(ua)
    ? 'Android'
    : isIpad(env)
      ? 'iPad'
      : /iPhone|iPod/.test(ua)
        ? 'iPhone'
        : /Windows/.test(ua)
          ? 'Windows'
          : /Mac OS X|Macintosh/.test(ua)
            ? 'Mac'
            : /CrOS/.test(ua)
              ? 'Chromebook'
              : /Linux/.test(ua)
                ? 'Linux'
                : 'otro aparato';
  const browser = /EdgA?\/|EdgiOS\//.test(ua)
    ? 'Edge'
    : /SamsungBrowser\//.test(ua)
      ? 'Samsung Internet'
      : /Firefox\/|FxiOS\//.test(ua)
        ? 'Firefox'
        : /OPR\//.test(ua)
          ? 'Opera'
          : /Chrome\/|CriOS\//.test(ua)
            ? 'Chrome'
            : /Safari\//.test(ua)
              ? 'Safari'
              : 'el navegador';
  return `${browser} en ${system}`;
}
